import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import type { Parameter } from "@/types/parameter"

interface ParameterSummaryBadgesProps {
  parameters?: Parameter[]
  isLoading?: boolean
}

/**
 * Filters parameters to match the Blazor app's ListDefinedParameters logic:
 * - Name starts with "udval" (case-insensitive)
 * - isVisible is true
 */
function getListDefinedParameters(parameters: Parameter[]): Parameter[] {
  return parameters.filter((p) => {
    const nameLower = p.name.toLowerCase()
    return nameLower.startsWith("udval") && p.isVisible
  })
}

/**
 * Filters to parameters that have meaningful values, matching Blazor's
 * WithParameters logic:
 * - BoolParameter where boolValue is true
 * - StringParameter where stringValue is not empty
 * - MultiSelectParameter (always shown with count)
 */
function getWithParameters(parameters: Parameter[]): Parameter[] {
  return parameters.filter((p) => {
    if (p.type === "Bool") return p.boolValue === true
    if (p.type === "String") return !!p.stringValue
    if (p.type === "MultiSelect") return p.stringValues.length > 0
    return false
  })
}

export function ParameterSummaryBadges({ parameters, isLoading = false }: Readonly<ParameterSummaryBadgesProps>) {
  if (isLoading && (!parameters || parameters.length === 0)) {
    return (
      <div className="flex flex-wrap gap-1">
        <Skeleton className="h-5 w-24 rounded-full" />
        <Skeleton className="h-5 w-32 rounded-full" />
      </div>
    )
  }

  if (!parameters || parameters.length === 0) {
    return null
  }

  const listDefined = getListDefinedParameters(parameters)
  const visible = getWithParameters(listDefined)

  if (visible.length === 0) {
    return null
  }

  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((param) => {
        // Bool parameters: just show the display name
        if (param.type === "Bool") {
          return (
            <Badge key={param.name} variant="outline" className="text-xs">
              {param.text}
            </Badge>
          )
        }

        // String parameters: show "DisplayName: ValueDisplayName"
        if (param.type === "String") {
          const selectedValue = param.availableValues.find(
            (av) => String(av.value) === param.stringValue,
          )
          const valueText = selectedValue?.name || param.stringValue
          return (
            <Badge key={param.name} variant="outline" className="text-xs">
              {param.text}: {valueText}
            </Badge>
          )
        }

        // MultiSelect parameters: show "DisplayName: count"
        if (param.type === "MultiSelect") {
          return (
            <Badge key={param.name} variant="outline" className="text-xs">
              {param.text}: {param.stringValues.length}
            </Badge>
          )
        }

        return null
      })}
    </div>
  )
}
